// components/process-units/SalesCompression.js
import React, { useState } from 'react';

const SalesCompression = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Sales Compression</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Sales compression increases the pressure of the processed gas to meet pipeline or 
            delivery requirements. This is typically the final step in the gas processing chain
            and can be a significant energy consumer depending on the pressure differential.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Discharge Pressure (bar)</label>
              <input
                type="number"
                min="10"
                max="150"
                step="1"
                value={config.dischargePressure || 80}
                onChange={(e) => handleInputChange('dischargePressure', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Compressor Efficiency (%)</label>
              <input
                type="number"
                min="50"
                max="95"
                step="1"
                value={(config.efficiency || 0.75) * 100}
                onChange={(e) => handleInputChange('efficiency', e.target.value / 100)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Compressor Type</label>
              <select 
                value={config.compressorType || 'centrifugal'}
                onChange={(e) => updateConfig({ compressorType: e.target.value })}
              >
                <option value="centrifugal">Centrifugal</option>
                <option value="reciprocating">Reciprocating</option>
                <option value="screw">Screw</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Driver Type</label>
              <select 
                value={config.driverType || 'gasTurbine'}
                onChange={(e) => updateConfig({ driverType: e.target.value })}
              >
                <option value="electricMotor">Electric Motor</option>
                <option value="gasTurbine">Gas Turbine</option>
                <option value="gasEngine">Gas Engine</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Number of Stages</label>
              <input
                type="number"
                min="1"
                max="5"
                step="1"
                value={config.stageCount || 2}
                onChange={(e) => handleInputChange('stageCount', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Intercooler Temperature (°C)</label>
              <input
                type="number"
                min="20"
                max="60"
                step="1"
                value={config.intercoolerTemp || 40}
                onChange={(e) => handleInputChange('intercoolerTemp', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Gas Cooling Temperature (°C)</label>
              <input
                type="number"
                min="10"
                max="60"
                step="1"
                value={config.coolingTemp || 40}
                onChange={(e) => handleInputChange('coolingTemp', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Aftercooler Type</label>
              <select 
                value={config.aftercoolerType || 'airCooled'}
                onChange={(e) => updateConfig({ aftercoolerType: e.target.value })}
              >
                <option value="airCooled">Air Cooled</option>
                <option value="waterCooled">Water Cooled</option>
                <option value="none">None</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Fuel/Power Source</label>
              <select 
                value={config.powerSource || 'processGas'}
                onChange={(e) => updateConfig({ powerSource: e.target.value })}
              >
                <option value="processGas">Process Gas</option>
                <option value="grid">Electric Grid</option>
                <option value="onsite">Onsite Power Generation</option>
              </select>
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>Power requirements increase with higher compression ratio and flow rate.</li>
              <li>Multiple stages with intercooling can significantly reduce energy requirements.</li>
              <li>Centrifugal compressors are more efficient for high flow rates with moderate pressure ratios.</li>
              <li>Reciprocating compressors are better for lower flow rates with high pressure ratios.</li>
              <li>Gas turbine drivers often have lower efficiency (30-40%) compared to electric motors (90-95%).</li>
              <li>Additional cooling may be required to meet pipeline temperature specifications.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCompression;